import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { selectUserAddress, selectUserPlaceId } from '../../redux/user/user.selectors';
import { addLocation, setAlertMessage, resetInteractiveMap } from '../../redux/user/user.actions';

import { selectStationData } from '../../redux/station-data/station-data.selectors';
import { setStationData } from '../../redux/station-data/station-data.actions';

import { getQueryParameterValuesByKey, addRelativeDistanceToStationData, uuidv4 } from '../../redux/station-data/station-data.utils';

import { TARGET, SEARCH } from '../../SVGs/svg.imports.js';


// Set this constants here so that it is not re-calculated with each component render
// Check if the browser geolocation API is available for determining the user's location
const geoLocationAvailable = ("geolocation" in navigator) ? true : false;

const mapDefaultCoordinates = {
  "lat": -32.1631277, // Geographic centre of NSW
  "lng": 147.0321395 // Geographic centre of NSW
};

// We use this to make our asynchronous requests for data
let xhrDataRequest = new XMLHttpRequest();

const LocationInput = ({ locationConfig, stationData, setStationDataFeed, userAddress, userPlaceId, addLocation, setAlertMessage, resetInteractiveMap }) => {
  const [ locationSuggestions, setLocationSuggestions ] = useState([]); // Used for the address search autocomplete suggestions
  const [ inputErrorToggle, setInputErrorToggle ] = useState(false); // Used to toggle the visibility of the address input error styling

  useEffect(() => {
    // When this component mounts, check for a supplied location in the query params.
    updateLocationFromQueryString();
  }, []);

  function updateLocationFromQueryString() {
    const queryString = window.location.href.split("?")[1] || "";
    let paramValuesArray = "";
    let hasRequiredValues = true;
    let regionName = null;

    // We need these values at a minimum in order to have a usable location.
    let suppliedLocation = {
      "fullAddress": null,
      "lat": null,
      "lng": null,
      "placeId": null
    };

    // First check for a "region" value
    regionName = getQueryParameterValuesByKey(queryString, 'region')[0] || null;

    if (regionName !== null) {
      // Reset the search address
      addLocation({
        "fullAddress": "",
        "placeID": "",
        "validAddress": false
      });

      // Exit this function
      return;
    }

    // Try to capture the required data from the page query string
    for (var prop in suppliedLocation) {
      // Check for a required query value
      paramValuesArray = getQueryParameterValuesByKey(queryString, prop)[0] || "";

      if (paramValuesArray !== "") {
        // Capture the supplied value
        suppliedLocation[prop] = paramValuesArray;
      } else {
        hasRequiredValues = false;
      }
    }

    // Update the application state with the supplied location
    // Also convert the address into a human-friendly format
    if (hasRequiredValues) {
      addLocation({
        "fullAddress": decodeURIComponent(suppliedLocation.fullAddress).replace(/\+/g, ' '),
        "latitude": suppliedLocation.lat,
        "longitude": suppliedLocation.lng,
        "placeID": suppliedLocation.placeId,
        "validAddress": true
      });
    }

    updateDistanceInStationData({
      "lat": parseFloat(suppliedLocation.lat),
      "lng": parseFloat(suppliedLocation.lng)
    });
  }

  /**
   * Respond to input on the location field
   *
   * @param {Change Event} evt Input Event that triggered the change
   */
  const onAddressChange = (evt) => {
    const { value } = evt.target;

    // Update the state with the user's input
    // Note that we don't reset the lat/long here because the map always requires a location to center on
    addLocation({
      "fullAddress": value,
      "placeID": "",
      "validAddress": false
    });

    // Reset the error styling
    setInputErrorToggle(false);

    if (value.length >= 3) {
      // Trigger the location suggestions dropdown
      getLocationSuggestions(value)
      .then( (data) => {
        // Check for any returned errors
        if (data.status === "OK") {
          // Address data was returned, update the input dropdown
          setLocationSuggestions(data.predictions.map( (prediction) => {
            return {
              "address": prediction.description,
              "place_id": prediction.place_id
            }
          }));
        } else {
          if (data.status === "ZERO_RESULTS") {
            setLocationSuggestions([{
              "address": "No locations found for that address",
              "place_id": ""
            }]);
          } else {
            // Likely a general error has occurred.
            setAlertMessage(`There has been a problem retrieving location data. The API returned an error message. Error: ${data.status}`);
          }
        }
      })
      .catch( (errorObject) => {
        if (errorObject.eventType === 'error') {
          setAlertMessage(`There has been a problem retrieving location data. Status code: ${errorObject.response.status}`);
        }
      });
    } else {
      // Remove the suggestions
      setLocationSuggestions([]);
    }

    // Show all locations
    if (value === "") {
      // Clear the address information to show all stations
      addLocation({
        "latitude": mapDefaultCoordinates.lat.toString(),
        "longitude": mapDefaultCoordinates.lng.toString(),
        "validAddress": true
      });

      updateDistanceInStationData(mapDefaultCoordinates);
    }
  }

  /**
   * Request some suggestions from the location API
   *
   * @param {String} input The address, or partial address, to lookup
   *
   * @returns Promise
   */
  function getLocationSuggestions(input) {
    let url = "";
    let sanitisedInput = "";
    let requestPromise = null;

    // Abort any current request
    xhrDataRequest.abort();

    // Generate a new request (this clears any current listeners)
    xhrDataRequest = new XMLHttpRequest();

    // Create our new Promise
    requestPromise = newRequestPromise(xhrDataRequest);

    // Clear up the address input
    sanitisedInput = encodeURIComponent(input).replace(/%20/g, '+');

    // Assemble the request URL
    url = `${locationConfig.googlePlacesApiEndpoint}?address=${sanitisedInput}`;

    // Set up our request
    xhrDataRequest.open("GET", url);

    // Make the request
    xhrDataRequest.send();

    return requestPromise;
  }

  /**
   * Respond to clicks on the address location suggestions
   *
   * @param {Click Event} evt Event that triggered the selection
   */
  const onSuggestionClick = (evt) => {
    const address = evt.target.value;
    const placeId = evt.target.getAttribute('data-placeid');

    evt.preventDefault();

    // If there is no place ID (e.g. invalid address) then show an error
    if (placeId === "") {
      setAlertMessage("Please search for an address.");
      return;
    }

    // Update the location
    updateOnAddressSuggestion(address, placeId);
  };

  /**
   * Given an address and place ID from the Google Places API, update the outage location
   *
   * @param {String} address The location address
   * @param {String} placeid The Google Places API Place ID value
   */
  function updateOnAddressSuggestion(address, placeId) {
    // Update the state with the selected suggestion
    // Note that we don't reset the lat/long here because the map always requires a location to center on
    addLocation({
      "fullAddress": address,
      "placeID": placeId,
      "validAddress": false
    });

    // Remove the suggestions
    setLocationSuggestions([]);

    // Make the request to get the address info (specifically, the latitude and longitude)
    getPlaceData(placeId)
      .then( (data) => {
        let latitude = 0;
        let longitude = 0;

        // Check for any returned errors
        if (data.status === "OK") {
          // Capture the lat/lng from the Place Details API
          latitude = data.result.geometry.location.lat;
          longitude = data.result.geometry.location.lng;

          addLocation({
            "latitude": latitude.toString(),
            "longitude": longitude.toString(),
            "validAddress": true
          });

          updateDistanceInStationData({
            "lat": latitude,
            "lng": longitude
          });
        } else {
          // Likely an error has occurred.
          setAlertMessage(`There has been a problem retrieving information about your selected address. The API returned an error message. Error: ${data.status}`);
        }
      })
      .catch( (errorObject) => {
        if (errorObject.eventType === 'error') {
          setAlertMessage(`There has been a problem retrieving information about your selected address. Status code: ${errorObject.response.status}`);
        }
      });
  }

  /**
   * Request further information on the user-selected address
   *
   * @param {String} placeid The Google Places API Place ID value
   *
   * @returns Promise
   */
  function getPlaceData(placeid) {
    let url = "";
    let requestPromise = null;

    // Abort any current request
    xhrDataRequest.abort();

    // Generate a new request (this clears any current listeners)
    xhrDataRequest = new XMLHttpRequest();

    // Create our new Promise
    requestPromise = newRequestPromise(xhrDataRequest);

    // Assemble the request URL
    url = `${locationConfig.googlePlaceDetailsApiEndpoint}?placeid=${placeid}`;

    // Set up our request
    xhrDataRequest.open("GET", url);

    // Make the request
    xhrDataRequest.send();

    return requestPromise;
  }

  /**
   * Respond to clicks on the location input "Reset" button
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onLocationResetClick = (evt) => {
    evt.preventDefault();

    // Remove the suggestions
    setLocationSuggestions([]);

    // Clear the location input
    // Reset the location to the defaults
    addLocation({
      "fullAddress": "",
      "placeID": "",
      "latitude": mapDefaultCoordinates.lat.toString(),
      "longitude": mapDefaultCoordinates.lng.toString(),
      "validAddress": false
    });

    updateDistanceInStationData(mapDefaultCoordinates);
  };

  /**
   * Respond to clicks on the location input "Submit" button
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onLocationSubmitClick = (evt) => {
    evt.preventDefault();

    // If there is no place ID (e.g. invalid address) then show an error
    if (userPlaceId === "" || userAddress === "") {
      // Show error message
      setAlertMessage(`There is no location found matching the address: "${userAddress}"`);

      // Set the error styling
      setInputErrorToggle(true);

      // Remove any suggestions
      setLocationSuggestions([]);
      return;
    }

    // If the user has been typing an address, and there are suggestions, take the first suggestion as their address
    if (locationSuggestions.length > 0) {
      updateOnAddressSuggestion(locationSuggestions[0].address, locationSuggestions[0].place_id);
    } else {
      // Re-center the map based on the user's currently selected address
      // Set a new ID to trigger the map to update with the current location
      resetInteractiveMap( uuidv4() );
    }
  };

  /**
   * Respond to clicks on the "Use my location" button
   * Note: To test this locally you need to access the page via: localhost
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onGeoLocateClick = (evt) => {
    evt.preventDefault();
    const geoLocateButton = evt.currentTarget;

    // Remove any suggestions from the text search
    setLocationSuggestions([]);

    // Add the loading styling
    geoLocateButton.classList.add('is-loading');

    // Request the user's location
    navigator.geolocation.getCurrentPosition(onGeoLocateSuccess, onGeoLocateFail);

    function onGeoLocateSuccess(position) {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      const latLong = `${latitude},${longitude}`;

      // Update the state with the returned lat/long co-ords
      addLocation({
        "latitude": latitude.toString(),
        "longitude": longitude.toString(),
        "validAddress": true
      });

      // Make the request to get possible locations for the user's lat/lng
      getLatLongData(latLong)
        .then( (data) => {
          // Take the first suggestion and update the state with the address
          if (data.results.length > 0) {
            // Update the state with the first suggestion
            addLocation({
              "fullAddress": data.results[0].formatted_address,
              "placeID": data.results[0].place_id
            });
          }

          updateDistanceInStationData({
            "lat": latitude,
            "lng": longitude
          });

          // Alert user of any errors
          if (data.error_message) {
            setAlertMessage(`There has been a problem retrieving your address information. Error: ${data.error_message}`);
          }

          // Remove the loading styling
          geoLocateButton.classList.remove('is-loading');
        })
        .catch( (errorObject) => {
          if (errorObject.eventType === 'error') {
            setAlertMessage(`There has been a problem retrieving your address information. Status code: ${errorObject.response.status}`);
          }

          // Remove the loading styling
          geoLocateButton.classList.remove('is-loading');
        });
    }

    function onGeoLocateFail() {
      setAlertMessage("We were unable to determine your location, you may need to configure your browser to allow this site to access your location. Otherwise, please try searching for your address.");

      // Remove the loading styling
      geoLocateButton.classList.remove('is-loading');
    }
  };

  /**
   * Request possible address locations from provided latitude and longitude co-ordinates
   *
   * @param {String} latLong The latitude and longitude co-ordinates to lookup. e.g. "-33.8666677,151.2021696"
   *
   * @returns Promise
   */
  function getLatLongData(latLong) {
    let url = "";
    let requestPromise = null;

    // Abort any current request
    xhrDataRequest.abort();

    // Generate a new request (this clears any current listeners)
    xhrDataRequest = new XMLHttpRequest();

    // Create our new Promise
    requestPromise = newRequestPromise(xhrDataRequest);

    // Assemble the request URL
    url = `${locationConfig.googleGeocodingApiEndpoint}?latlng=${latLong}`;

    // Set up our request
    xhrDataRequest.open("GET", url);

    // Make the request
    xhrDataRequest.send();

    return requestPromise;
  }

  /**
   * Create a new Promise that we can use to response to asynchronus XML Http Requests
   *
   * @param {XMLHttpRequest} xhrRequest The XMLHttpRequest to attach the event listeners to
   *
   * @returns Promise
   */
  function newRequestPromise(xhrRequest) {
    return new Promise( (resolve, reject) => {
      // Respond to requests to GET the data
      xhrRequest.addEventListener('load', function(evt) {
        if (evt.currentTarget.status === 200) {
          // We got a good response
          resolve(JSON.parse(evt.currentTarget.response));
        } else {
          // We got a bad response (e.g. 403, 404)
          reject( {
            "response": evt.currentTarget,
            "eventType": 'error'
            }
          );
        }
      });

      // Respond to request errors
      xhrRequest.addEventListener('error', function(evt) {
        reject( {
          "response": evt.currentTarget,
          "eventType": 'error'
          }
        );
      });

      // Respond to request aborts
      xhrRequest.addEventListener('abort', function(evt) {
        reject( {
          "response": evt.currentTarget,
          "eventType": 'abort'
          }
        );
      });
    });
  }

  /**
   * Update the station data with distances from the user's selected location
   * (or from the default location)
   *
   * @param {Object} originPoint Key-Value pairs of "lat" (latitude) and "lng" (longitude); where the value is a Number
   *
   * @returns void
   */
  function updateDistanceInStationData(originPoint) {
    let modifiedStationData = stationData; // "stationData" is a prop passed into this Component from REDUX

    if (modifiedStationData.length > 0) {
      modifiedStationData = addRelativeDistanceToStationData(originPoint, modifiedStationData);

      // Save the updated data to the REDUX state
      setStationDataFeed(modifiedStationData);
    }
  }

  /**
   * Generate the CSS Class attribute values for the address input
   *
   * @returns String
   */
  const generateInputClasses = () => {
    let cssClasses = 'station-map-location-inputs__input';

    if (locationSuggestions.length !== 0) {
      cssClasses = cssClasses + ' is-active';
    }

    if (inputErrorToggle) {
      cssClasses = cssClasses + ' has-error';
    }

    return cssClasses;
  };

  return (
    <div className="station-map-location-inputs__container">
      <form onSubmit={(evt) => evt.preventDefault()} className="station-map-location-inputs__form">
        <label className="station-map-location-inputs__label visuallyhidden" htmlFor="address-input">Enter your address</label>
        <input className={generateInputClasses()} id="address-input" type="text" name="location" value={userAddress} onChange={onAddressChange} placeholder="Find your nearest station" autoComplete="off" spellCheck="false" />
        <div className="station-map-location-suggestions__container">
          <ol className="station-map-location-suggestions__list">
            {
              locationSuggestions.map((suggestion) => {
                return (
                  <li key={suggestion.place_id} className="station-map-location-suggestions__list-item">
                    <button className="station-map-location-suggestions__button" onClick={onSuggestionClick} value={suggestion.address} data-placeid={suggestion.place_id}>{suggestion.address}</button>
                  </li>
                )
              })
            }
          </ol>
        </div>
        {
          (userAddress) ?
            <button onClick={onLocationResetClick} className="station-map-location-inputs-reset__button" title="Clear search"><span className="station-map-location-inputs__close-bar"></span><span className="station-map-location-inputs__close-bar"></span><span className="visuallyhidden">Reset the form</span></button>
          : <button onClick={onGeoLocateClick} className="station-map-location-inputs-location__button" disabled={!geoLocationAvailable}><span className="station-map-location-inputs-location__icon"><TARGET/></span><span className="visuallyhidden">Use my location</span><span className="station-map-location-inputs-location__loading-message">Searching...</span></button>
        }
        <button onClick={onLocationSubmitClick} className="station-map-location-inputs-submit__button"><span className="visuallyhidden">Search</span><span className="station-map-location-inputs-submit__icon"><SEARCH/></span></button>
      </form>
    </div>
  );
};

LocationInput.propTypes = {
  "locationConfig": PropTypes.object,
  "stationData": PropTypes.array,
  "setStationDataFeed": PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "userAddress": PropTypes.string,
  "userPlaceId": PropTypes.string,
  "addLocation": PropTypes.oneOfType( [PropTypes.object, PropTypes.func] ),
  "setAlertMessage": PropTypes.oneOfType( [PropTypes.string, PropTypes.func] ),
  "resetInteractiveMap": PropTypes.oneOfType( [PropTypes.string, PropTypes.func] )
};

const mapStateToProps = (state) => {
  return {
    "stationData": selectStationData(state),
    "userAddress": selectUserAddress(state),
    "userPlaceId": selectUserPlaceId(state)
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    "setStationDataFeed": stationData => dispatch(setStationData(stationData)),
    "addLocation": location => dispatch(addLocation(location)),
    "setAlertMessage": message => dispatch(setAlertMessage(message)),
    "resetInteractiveMap": uniqueId => dispatch(resetInteractiveMap(uniqueId))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationInput);